import { useEffect, useState } from 'react';
import { nextTypeCycle, TypeCycleState } from './heroTypeCycle';
import type { HeroSlide } from './heroSlides';

const INITIAL_CYCLE: TypeCycleState = { phraseIndex: 0, charCount: 0, phase: 'type' };

function prefersReducedMotion(): boolean {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * The hero's one typing timer (WU-HERO). The headline and the line under it both read this state,
 * so they cannot drift apart. Under reduced motion it stops, and the hero shows slide 1.
 */
export function useHeroCycle(slides: HeroSlide[]): { reduced: boolean; cycle: TypeCycleState } {
  const [reduced, setReduced] = useState(prefersReducedMotion);
  const [cycle, setCycle] = useState<TypeCycleState>(INITIAL_CYCLE);

  useEffect(() => {
    const media = window.matchMedia('(prefers-reduced-motion: reduce)');
    const onChange = () => {
      const matches = media.matches;
      setReduced(matches);
      if (!matches) {
        setCycle(INITIAL_CYCLE);
      }
    };
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, []);

  // A language switch changes every phrase, so start again from slide 1. Keyed on the text itself, so a new
  // array with the same words never restarts the cycle.
  const signature = slides.map((slide) => slide.prefix + slide.accent + slide.suffix).join('|');
  useEffect(() => {
    setCycle(INITIAL_CYCLE);
  }, [signature]);

  useEffect(() => {
    if (reduced) {
      return;
    }
    const slide = slides[cycle.phraseIndex] ?? slides[0];
    const phraseLength = (slide.prefix + slide.accent + slide.suffix).length;
    const { state: next, delayMs } = nextTypeCycle(cycle, slides.length, phraseLength);
    const timeoutId = window.setTimeout(() => setCycle(next), delayMs);
    return () => window.clearTimeout(timeoutId);
  }, [reduced, cycle, slides]);

  return { reduced, cycle };
}
